// frontend/src/components/LeadForm.jsx

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Confetti from 'react-confetti';
import { Oval } from 'react-loader-spinner';

export default function LeadForm({ agent }) {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    mortgageType: "",
    otherMortgageDescription: "",
    propertyAddress: "",
    propertyType: "",
    propertyValue: "",
    downPayment: "",
    realtor: "",
    comments: "",
    hasAdditionalOwners: false,
    ownersList: "",
    householdIncome: "",
    rentalIncome: "",
    otherIncome: "",
    creditCardDebt: "",
    carLoanDebt: "",
    securedLoanDebt: "",
    unsecuredLoanDebt: "",
    existingProperties: "",
    consent: false
  });

  const [loading, setLoading]       = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const mortgageOptions = [
    "First-Time Home Buyer",
    "Mortgage Pre-Approval",
    "Refinance Existing Mortgage",
    "Mortgage Renewal",
    "Investment Property Mortgage",
    "Self-Employed Mortgage",
    "New to Canada Mortgage",
    "Commercial Mortgage",
    "HELOC (Home Equity Line of Credit)",
    "Reverse Mortgage",
    "Other"
  ];

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const validate = () => {
    // Required basic fields
    if (!form.fullName.trim()) {
      toast.error("Full Name is required.");
      return false;
    }
    if (!form.email.trim()) {
      toast.error("Email is required.");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return false;
    }
    if (!form.phone.trim()) {
      toast.error("Phone number is required.");
      return false;
    }
    const phoneRegex = /^\+?[\d\s()-]{10,15}$/;
    if (!phoneRegex.test(form.phone)) {
      toast.error("Please enter a valid phone number (e.g. 416-555-1234).");
      return false;
    }
    if (!form.mortgageType) {
      toast.error("Please select a mortgage type.");
      return false;
    }
    if (form.mortgageType === "Other" && !form.otherMortgageDescription.trim()) {
      toast.error("Please describe your mortgage need.");
      return false;
    }
    if (!form.propertyAddress.trim()) {
      toast.error("Please enter the subject property address.");
      return false;
    }
    if (!form.propertyType) {
      toast.error("Please select the subject property type.");
      return false;
    }
    if (form.hasAdditionalOwners && !form.ownersList.trim()) {
      toast.error("Please specify the additional owners’ names.");
      return false;
    }
    if (!form.consent) {
      toast.error("You must consent to share your information.");
      return false;
    }
    // Check numeric fields ≥ 0
    const numericFields = [
      "propertyValue",
      "downPayment",
      "householdIncome",
      "rentalIncome",
      "otherIncome",
      "creditCardDebt",
      "carLoanDebt",
      "securedLoanDebt",
      "unsecuredLoanDebt"
    ];
    for (const field of numericFields) {
      if (form[field] && Number(form[field]) < 0) {
        toast.error(`${field.replace(/([A-Z])/g, " $1")} cannot be negative.`);
        return false;
      }
    }
    return true;
  };

  const onSubmit = async () => {
    if (!validate()) return;

    const payload = {
      agentName:        agent.name,
      agentFSRALicense: agent.fsra_license,
      leadSource:       "Chatbot Lead Capture",
      leadDetails: {
        fullName:                 form.fullName.trim(),
        email:                    form.email.trim().toLowerCase(),
        phone:                    form.phone.trim(),
        mortgageType:             form.mortgageType,
        otherMortgageDescription: form.otherMortgageDescription.trim(),
        propertyAddress:          form.propertyAddress.trim(),
        propertyType:             form.propertyType,
        propertyValue:            form.propertyValue ? Number(form.propertyValue) : 0,
        downPayment:              form.downPayment ? Number(form.downPayment) : 0,
        realtor:                  form.realtor,
        comments:                 form.comments.trim(),
        hasAdditionalOwners:      form.hasAdditionalOwners,
        ownersList:               form.ownersList.trim(),
        householdIncome:          form.householdIncome ? Number(form.householdIncome) : 0,
        rentalIncome:             form.rentalIncome ? Number(form.rentalIncome) : 0,
        otherIncome:              form.otherIncome ? Number(form.otherIncome) : 0,
        creditCardDebt:           form.creditCardDebt ? Number(form.creditCardDebt) : 0,
        carLoanDebt:              form.carLoanDebt ? Number(form.carLoanDebt) : 0,
        securedLoanDebt:          form.securedLoanDebt ? Number(form.securedLoanDebt) : 0,
        unsecuredLoanDebt:        form.unsecuredLoanDebt ? Number(form.unsecuredLoanDebt) : 0,
        existingProperties:       form.existingProperties.trim(),
        consent:                  form.consent
      }
    };

    try {
      setLoading(true);
      await axios.post('/api/leads', payload);

      toast.success("Thank you! 🎉 Redirecting you shortly…");
      setShowConfetti(true);

      // Reset form fields
      setForm({
        fullName: "",
        email: "",
        phone: "",
        mortgageType: "",
        otherMortgageDescription: "",
        propertyAddress: "",
        propertyType: "",
        propertyValue: "",
        downPayment: "",
        realtor: "",
        comments: "",
        hasAdditionalOwners: false,
        ownersList: "",
        householdIncome: "",
        rentalIncome: "",
        otherIncome: "",
        creditCardDebt: "",
        carLoanDebt: "",
        securedLoanDebt: "",
        unsecuredLoanDebt: "",
        existingProperties: "",
        consent: false
      });

      // After 1.5s, navigate to /thank-you (while confetti shows for 4s)
      setTimeout(() => {
        setShowConfetti(false);
        navigate('/thank-you');
      }, 1500);
    } catch (err) {
      console.error('Error submitting lead:', err);
      toast.error("Submission failed. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={containerStyle}>
      {showConfetti && <Confetti numberOfPieces={400} recycle={false} />}
      <h2 style={titleStyle}>📝 Quick Mortgage Inquiry</h2>

      {/* Full Name, Email, Phone */}
      {["fullName", "email", "phone"].map((field, idx) => (
        <div key={idx} style={{ marginBottom: '16px' }}>
          <input
            type={field === "email" ? "email" : field === "phone" ? "tel" : "text"}
            name={field}
            placeholder={
              field === "fullName" ? "Full Name *"
                : field === "email"   ? "Email *"
                : "Phone * (e.g. 416-555-1234)"
            }
            value={form[field]}
            onChange={onChange}
            style={inputStyle}
            required={field !== 'comments'}
          />
        </div>
      ))}

      {/* Mortgage Type */}
      <div style={{ marginBottom: '16px' }}>
        <select
          name="mortgageType"
          value={form.mortgageType}
          onChange={onChange}
          style={inputStyle}
          required
        >
          <option value="">Select Mortgage Type *</option>
          {mortgageOptions.map((opt, i) => (
            <option key={i} value={opt}>{opt}</option>
          ))}
        </select>
      </div>

      {/* If Other */}
      {form.mortgageType === "Other" && (
        <div style={{ marginBottom: '16px' }}>
          <input
            type="text"
            name="otherMortgageDescription"
            placeholder="Please describe your mortgage need *"
            value={form.otherMortgageDescription}
            onChange={onChange}
            style={inputStyle}
            required
          />
        </div>
      )}

      {/* Property Address */}
      <div style={{ marginBottom: '16px' }}>
        <input
          type="text"
          name="propertyAddress"
          placeholder="Subject Property Address *"
          value={form.propertyAddress}
          onChange={onChange}
          style={inputStyle}
          required
        />
      </div>

      {/* Property Type */}
      <div style={{ marginBottom: '16px' }}>
        <select
          name="propertyType"
          value={form.propertyType}
          onChange={onChange}
          style={inputStyle}
          required
        >
          <option value="">Select Property Type *</option>
          <option value="Single-Family">Single‐Family Home</option>
          <option value="Condo">Condominium</option>
          <option value="Townhouse">Townhouse</option>
          <option value="Multi-Unit">Multi‐Unit (2–4 units)</option>
          <option value="Commercial">Commercial</option>
          <option value="Land">Vacant Land</option>
          <option value="Other">Other</option>
        </select>
      </div>

      {/* Property Value & Down Payment */}
      {["propertyValue", "downPayment"].map((field, idx) => (
        <div key={idx} style={{ marginBottom: '16px' }}>
          <input
            type="number"
            name={field}
            placeholder={ field === "propertyValue" ? "Property Value ($)" : "Down Payment ($)" }
            value={form[field]}
            onChange={onChange}
            style={inputStyle}
          />
        </div>
      ))}

      {/* Realtor Selector */}
      <div style={{ marginBottom: '16px' }}>
        <select
          name="realtor"
          value={form.realtor}
          onChange={onChange}
          style={inputStyle}
        >
          <option value="">Working with Realtor? (Optional)</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      </div>

      {/* Comments */}
      <div style={{ marginBottom: '16px' }}>
        <textarea
          name="comments"
          placeholder="Additional Comments (Optional)"
          value={form.comments}
          onChange={onChange}
          rows="3"
          style={{ ...inputStyle, resize: "vertical" }}
        />
      </div>

      {/* Additional Owners */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ marginRight: '8px' }}>Any Additional Owners? *</label>
        <select
          name="hasAdditionalOwners"
          value={form.hasAdditionalOwners ? "Yes" : "No"}
          onChange={(e) => {
            const val = e.target.value === "Yes";
            setForm(prev => ({ ...prev, hasAdditionalOwners: val, ownersList: "" }));
          }}
          style={inputStyle}
        >
          <option value="No">No</option>
          <option value="Yes">Yes</option>
        </select>
      </div>
      {form.hasAdditionalOwners && (
        <div style={{ marginBottom: '16px' }}>
          <textarea
            name="ownersList"
            placeholder="List additional owners (Name(s))"
            value={form.ownersList}
            onChange={onChange}
            rows="2"
            style={{ ...inputStyle, resize: "vertical" }}
            required
          />
        </div>
      )}

      {/* Income & Debt Fields */}
      <h4 style={{ marginBottom: '12px', color: '#2c3e50' }}>Income & Liabilities (Optional)</h4>
      {[
        { name: "householdIncome", label: "Household Income ($)" },
        { name: "rentalIncome", label: "Monthly Rental Income ($)" },
        { name: "otherIncome", label: "Other Income ($)" },
        { name: "creditCardDebt", label: "Credit Card Debt ($)" },
        { name: "carLoanDebt", label: "Car Loan Debt ($)" },
        { name: "securedLoanDebt", label: "Secured Loan Debt ($)" },
        { name: "unsecuredLoanDebt", label: "Unsecured Loan Debt ($)" },
        { name: "existingProperties", label: "Existing Properties (Address/Brief)" }
      ].map((fieldDef, idx) => (
        <div key={idx} style={{ marginBottom: '16px' }}>
          <input
            type={ fieldDef.name.includes("Income") || fieldDef.name.includes("Debt") ? "number" : "text" }
            name={fieldDef.name}
            placeholder={fieldDef.label}
            value={form[fieldDef.name]}
            onChange={onChange}
            style={inputStyle}
          />
        </div>
      ))}

      {/* Consent Checkbox */}
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
        <input
          type="checkbox"
          name="consent"
          checked={form.consent}
          onChange={onChange}
          style={{ marginRight: '8px' }}
        />
        <label style={{ fontSize: '14px', color: '#555' }}>
          I consent to share my information with my mortgage agent.
        </label>
      </div>

      {/* Submit Button or Loader */}
      <div style={{ textAlign: 'center' }}>
        {loading ? (
          <Oval
            height={30}
            width={30}
            color="#007bff"
            secondaryColor="#007bff"
            strokeWidth={3}
            visible={true}
            ariaLabel="loading"
          />
        ) : (
          <button onClick={onSubmit} style={buttonStyle}>
            Submit My Details
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────
const containerStyle = {
  background: "#ffffff",
  padding: "30px",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  maxWidth: "600px",
  margin: "auto"
};

const titleStyle = {
  textAlign: "center",
  marginBottom: "24px",
  color: "#2c3e50",
  fontSize: "28px"
};

const inputStyle = {
  width: "100%",
  padding: "12px",
  fontSize: "16px",
  borderRadius: "8px",
  border: "1px solid #ccc",
  outline: "none",
  marginBottom: "8px"
};

const buttonStyle = {
  padding: "12px 24px",
  backgroundColor: "#007bff",
  color: "white",
  fontWeight: "bold",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "16px"
};
